<template>
	<div>
		<label>
			Manual create e-Coupon
		</label>
		<p class="typo-body-2 color-black-45 mb-4">
			Create code by importing file. Support .xls, xlsx with the maximum file of 10 MB
		</p>
		<CRow>
			<CCol>
				<CButton
					:disabled="isImporting || !hasEditPermission"
					class="transparent"
					color="secondary"
					data-test-id="import-coupon-code"
					@click="importCouponCode"
				>
					{{ isImporting ? 'Importing...' : 'Import .xlsx' }}
				</CButton>
			</CCol>
		</CRow>
		<CRow
			v-if="showCouponList"
			class="mt-4"
		>
			<CCol md="12">
				<p class="typo-body-2 mt-2 mb-2">
					Total {{ totalCouponCodes }} code(s)
				</p>
			</CCol>
			<CCol md="12">
				<div class="coupon-codes p-3">
					<p class="typo-caption-2 mb-0">{{ couponCodesText }}</p>
				</div>
			</CCol>
			<CCol v-if="!isSingleCode" md="12" class="mt-3">
				<div>
					<CouponBadge :label="couponCodePattern" />
					<span class="typo-caption-2">{{ `If it’s multi-code, the code will be displayed as "${couponCodePattern}" on storefront.` }}</span>
				</div>
			</CCol>
		</CRow>
	</div>
</template>

<script>
import { mapActions } from 'vuex';

import CouponBadge from '@/components/CouponBadge.vue';

import { importCouponCodesAPI } from '../services/api/promotions.api';

import {
	MAXIMUM_COUPON_QUANTITY,
	MAXIMUM_COUPON_IMPORT_FILE_SIZE,
	MAXIMUM_COUPON_IMPORT_FILE_SIZE_LABEL,
} from '../enums/promotions';

import { getECouponCodePattern, numberFormat } from '../assets/js/helpers';
import isHavePermission from '../mixins/isHavePermission';

export default {
	name: 'PromotionECouponManual',

	components: {
		CouponBadge,
	},

	mixins: [isHavePermission],

	props: {
		dataECoupon: {
			type: Object,
			default: null,
		},
		isEditMode: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		const { couponCodes = [] } = this.dataECoupon || {};

		return {
			isImporting: false,
			formData: {
				couponCodes,
				newCouponCodes: [],
			},
		};
	},

	computed: {
		mergeCouponCodes() {
			if (Array.isArray(this.formData.couponCodes) && Array.isArray(this.formData.newCouponCodes)) {
				return [...new Set([...this.formData.couponCodes, ...this.formData.newCouponCodes])];
			}

			return [];
		},
		// ต้องอ่านจาก mergeCouponCodes ให้ตรงกับ `Total N code(s)` ที่แสดงคู่กัน
		couponCodesText() {
			const codes = this.mergeCouponCodes;

			if (!codes.length) {
				return '';
			}

			const maximumValueList = 5;

			if (codes.length > maximumValueList) {
				return `${codes.slice(0, maximumValueList).join(', ')} and ${(codes.length - maximumValueList)} more codes`;
			}

			return codes.join(', ');
		},
		codePrefix() {
			if (!this.formData.couponCodes || !Array.isArray(this.formData.couponCodes)) {
				return null;
			}

			// First 3 charecters will be Prefix value
			const defaultPrefixLength = 3;
			const firstCouponCode = this.formData.couponCodes[0];

			if (!firstCouponCode) {
				return null;
			}

			// If code length longer than default, substring
			// If not, use the code to be prefix
			return firstCouponCode.length > defaultPrefixLength
				? firstCouponCode.substring(0, defaultPrefixLength)
				: firstCouponCode;
		},
		showCouponList() {
			return this.mergeCouponCodes.length > 0;
		},
		totalCouponCodes() {
			return this.mergeCouponCodes.length;
		},
		isSingleCode() {
			return this.totalCouponCodes === 1;
		},
		codeSuffixLength() {
			if (this.totalCouponCodes > 0 && this.codePrefix) {
				return (this.mergeCouponCodes[0].length - this.codePrefix.length);
			}

			return 0;
		},
		couponCodePattern() {
			return getECouponCodePattern(this.codePrefix, this.codeSuffixLength, this.mergeCouponCodes); // sample result is "BNNXXX"
		},
	},

	watch: {
		dataECoupon: {
			deep: true,
			handler(data) {
				// Watch props data change and update this
				this.formData.newCouponCodes = data.newCouponCodes || [];
				this.formData.couponCodes = data.couponCodes || [];
			},
		},
	},

	methods: {
		...mapActions({
			showToast: 'toast/showToast',
		}),
		emitECouponData() {
			const onlyNewCouponCodes = this.isEditMode
				? this.formData.newCouponCodes.filter((code) => !this.formData.couponCodes.includes(code))
				: [];

			this.$emit('update:ecoupon', {
				prefix: this.codePrefix,
				length: this.codeSuffixLength,
				quantity: this.mergeCouponCodes.length,
				couponCodes: this.mergeCouponCodes, // Need to keep current pattern
				newCouponCodes: onlyNewCouponCodes,
			});
		},
		importCouponCode() {
			const fileSelector = document.createElement('input');
			fileSelector.setAttribute('type', 'file');
			fileSelector.setAttribute('accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel');
			fileSelector.onchange = async () => {
				const file = fileSelector.files.item(0);

				// UI ประกาศไว้ว่ารับไม่เกิน 10 MB — เดิมไม่เคยเช็คเลย ปล่อยไปตายที่ BE
				if (file && file.size > MAXIMUM_COUPON_IMPORT_FILE_SIZE) {
					this.showToast({
						content: `File size must not exceed ${MAXIMUM_COUPON_IMPORT_FILE_SIZE_LABEL}.`,
						header: 'Failure',
						type: 'danger',
					});

					return;
				}

				const params = {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				};

				this.isImporting = true;

				try {
					const res = await importCouponCodesAPI(file, params);
					// เดิมใช้ `[...res?.data?.data] || []` ซึ่ง throw ที่ spread ก่อน `||` จะได้ทำงาน
					const importedCodes = Array.isArray(res?.data?.data) ? [...res.data.data] : [];

					const nextCouponCodes = this.isEditMode ? this.formData.couponCodes : importedCodes;
					// import ซ้ำต้องสะสมต่อ ไม่ใช่ทับของรอบก่อน (ให้ตรงกับฝั่ง Auto)
					const nextNewCouponCodes = this.isEditMode
						? [...this.formData.newCouponCodes, ...importedCodes]
						: [];

					// ฝั่ง AUTO มีเพดาน 50,000 อยู่ที่ validation ของ quantity แต่ฝั่ง MANUAL
					// ไม่เคยถูกกันไว้เลย ไฟล์ใหญ่ ๆ เลยหลุดขึ้นไปถึง BE ได้
					if (new Set([...nextCouponCodes, ...nextNewCouponCodes]).size > MAXIMUM_COUPON_QUANTITY) {
						this.showToast({
							content: `Total coupon codes must not exceed ${numberFormat(MAXIMUM_COUPON_QUANTITY)}.`,
							header: 'Failure',
							type: 'danger',
						});

						return;
					}

					this.formData.couponCodes = nextCouponCodes;
					this.formData.newCouponCodes = nextNewCouponCodes;

					// emit ทุกครั้งที่สำเร็จ แม้ผลลัพธ์จะว่าง เพื่อให้ state ฝั่ง parent ตรงกับที่แสดงอยู่เสมอ
					this.emitECouponData();

					this.showToast({
						content: 'Coupon codes successfully uploaded.',
						header: 'Success',
						type: 'success',
					});
				} catch (e) {
					this.showToast({
						content: 'Coupon codes upload failed',
						header: 'Failure',
						type: 'danger',
					});
				} finally {
					this.isImporting = false;
				}
			};
			fileSelector.click();
		},
	},
};
</script>

<style lang="scss" scoped>
	.coupon-codes {
		border-radius: rem(4);
		border: rem(1) solid $color-gray-400;
		background: $color-white;
	}
</style>
